import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Users, UserCheck, UserX, Shield, Heart, MapPin, Search, Plus } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { GamePositionManager } from './GamePositionManager';
import { PlayerStatusBadge } from './PlayerStatusBadge';
import { useUserRole } from '@/hooks/useUserRole';
import { getUserTypeDisplayName } from '@/utils/userTypeDisplay';

interface TeamMember {
  user_id: string;
  member_type: 'player' | 'coach' | 'support';
  position?: string;
  role?: string;
  game_day_status?: 'ready_to_play' | 'not_ready' | 'no_show' | 'injured' | 'substituted';
  user_details?: {
    username?: string;
    first_name?: string;
    last_name?: string;
    avatar_url?: string;
    user_type?: 'supabase' | 'internal' | 'unknown';
    role?: string;
  };
}

interface CoachRosterManagementProps {
  isOpen: boolean;
  onClose: () => void;
  gameId: string;
  currentUserId: string;
  game?: {
    game_name: string;
    game_code?: string;
  };
}

export const CoachRosterManagement = ({ 
  isOpen, 
  onClose, 
  gameId, 
  currentUserId,
  game
}: CoachRosterManagementProps) => {
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [canManage, setCanManage] = useState(false);
  const [showPositionManager, setShowPositionManager] = useState(false);
  const [userTeam, setUserTeam] = useState<{ teamId: number; teamName: string; teamSide: 'home' | 'away' } | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<TeamMember[]>([]);
  const [searching, setSearching] = useState(false);
  const [showAddPlayers, setShowAddPlayers] = useState(false);

  const { permissions } = useUserRole();

  useEffect(() => {
    if (isOpen) {
      findUserTeamForGame();
    }
  }, [isOpen, gameId, currentUserId]);

  useEffect(() => {
    if (userTeam) {
      checkManagementPermissions();
      loadTeamMembers();
      loadExistingRoster();
    }
  }, [userTeam]);

  const findUserTeamForGame = async () => {
    try {
      // First get the game details
      const { data: game, error: gameError } = await supabase
        .from('games')
        .select('home_team_id, away_team_id')
        .eq('id', gameId)
        .single();

      if (gameError) {
        // Try venue_games table
        const { data: venueGame, error: venueGameError } = await supabase
          .from('venue_games')
          .select('home_team_id, away_team_id')
          .eq('id', gameId)
          .single();

        if (venueGameError) throw venueGameError;
        
        // Check if user is coach/owner of either team
        await checkUserTeamRole(venueGame.home_team_id, venueGame.away_team_id);
        return;
      }

      // Check if user is coach/owner of either team
      await checkUserTeamRole(game.home_team_id, game.away_team_id);
    } catch (error) {
      console.error('Error finding user team:', error);
      toast.error('Failed to determine your team role');
    }
  };

  const checkUserTeamRole = async (homeTeamId: number | null, awayTeamId: number | null) => {
    try {
      const teamIds = [homeTeamId, awayTeamId].filter(Boolean) as number[];
      
      for (const teamId of teamIds) {
        // Check if user is team owner
        const { data: team } = await supabase
          .from('teams')
          .select('team_name, created_by')
          .eq('team_id', teamId)
          .single();

        if (team?.created_by === currentUserId) {
          setUserTeam({
            teamId,
            teamName: team.team_name,
            teamSide: teamId === homeTeamId ? 'home' : 'away'
          });
          return;
        }

        // Check if user is a coach
        const { data: coach } = await supabase
          .from('team_coaches')
          .select('coach_id')
          .eq('team_id', teamId)
          .eq('coach_id', currentUserId)
          .single();

        if (coach) {
          setUserTeam({
            teamId,
            teamName: team?.team_name || 'Team',
            teamSide: teamId === homeTeamId ? 'home' : 'away'
          });
          return;
        }
      }

      // If no team role found, show error
      if (!['venue', 'admin'].includes(permissions.role)) {
        throw new Error('You are not a coach or owner of any team in this game');
      }
    } catch (error) {
      console.error('Error checking team role:', error);
      toast.error('You do not have permission to manage rosters for this game');
      onClose();
    }
  };

  const checkManagementPermissions = async () => {
    if (!userTeam) return;

    try {
      // Venue owners and admins can always manage
      if (['venue', 'admin'].includes(permissions.role)) {
        setCanManage(true);
        return;
      }

      // Check if user is team owner
      const { data: team } = await supabase
        .from('teams')
        .select('created_by')
        .eq('team_id', userTeam.teamId)
        .single();

      if (team?.created_by === currentUserId) {
        setCanManage(true);
        return;
      }

      // Check if user is a coach
      const { data: coach } = await supabase
        .from('team_coaches')
        .select('coach_id')
        .eq('team_id', userTeam.teamId)
        .eq('coach_id', currentUserId)
        .single();

      setCanManage(!!coach);
    } catch (error) {
      console.error('Error checking permissions:', error);
      setCanManage(false);
    }
  };

  const loadTeamMembers = async () => {
    if (!userTeam) return;

    try {
      setLoading(true);
      const allMembers: TeamMember[] = [];

      // First load existing game roster players (these are already selected)
      const { data: gameRosterPlayers, error: gameRosterError } = await supabase
        .from('game_rosters')
        .select('player_id, position, player_type')
        .eq('game_id', gameId)
        .eq('team_side', userTeam.teamSide);

      if (gameRosterError) throw gameRosterError;

      // Mark all game roster players as selected
      const gameRosterPlayerIds = gameRosterPlayers?.map(p => p.player_id) || [];
      setSelectedMembers(gameRosterPlayerIds);

      // Add game roster players to team members list
      for (const rosterPlayer of gameRosterPlayers || []) {
        const userDetails = await getUserDetails(rosterPlayer.player_id);
        const avatarUrl = await getUserAvatarUrl(rosterPlayer.player_id);
        allMembers.push({
          user_id: rosterPlayer.player_id,
          member_type: 'player',
          position: rosterPlayer.position,
          user_details: { ...userDetails, avatar_url: avatarUrl }
        });
      }

      // Load additional team players (not in game roster yet)
      let teamPlayersQuery = supabase
        .from('team_players')
        .select('*')
        .eq('team_id', userTeam.teamId);

      // Only filter out existing roster players if there are any
      if (gameRosterPlayerIds.length > 0) {
        teamPlayersQuery = teamPlayersQuery.not('user_id', 'in', `(${gameRosterPlayerIds.map(id => `"${id}"`).join(',')})`);
      }

      const { data: teamPlayers, error: playersError } = await teamPlayersQuery;

      if (playersError && playersError.code !== 'PGRST116') throw playersError;

      for (const player of teamPlayers || []) {
        const userDetails = await getUserDetails(player.user_id);
        const avatarUrl = await getUserAvatarUrl(player.user_id);
        allMembers.push({
          user_id: player.user_id,
          member_type: 'player',
          position: player.position,
          user_details: { ...userDetails, avatar_url: avatarUrl }
        });
      }

      // Load team coaches
      const { data: coaches, error: coachesError } = await supabase
        .from('team_coaches')
        .select('*')
        .eq('team_id', userTeam.teamId);

      if (coachesError) throw coachesError;

      for (const coach of coaches || []) {
        const userDetails = await getUserDetails(coach.coach_id);
        const avatarUrl = await getUserAvatarUrl(coach.coach_id);
        allMembers.push({
          user_id: coach.coach_id,
          member_type: 'coach',
          role: coach.role,
          user_details: { ...userDetails, avatar_url: avatarUrl }
        });
      }

      setTeamMembers(allMembers);
    } catch (error) {
      console.error('Error loading team members:', error);
      toast.error('Failed to load team members');
    } finally {
      setLoading(false);
    }
  };

  const loadExistingRoster = async () => {
    // This function is now integrated into loadTeamMembers to avoid duplicate API calls
    // The existing roster is loaded automatically when loadTeamMembers runs
  };

  const getUserDetails = async (userId: string) => {
    try {
      // Try Supabase profiles first
      const { data: profile } = await supabase
        .from('profiles')
        .select('username, first_name, last_name, role')
        .eq('id', userId)
        .maybeSingle();

      if (profile) {
        return {
          username: profile.username,
          first_name: profile.first_name,
          last_name: profile.last_name,
          user_type: 'supabase' as const,
          role: profile.role
        };
      }

      // Try internal users
      const { data: internalUser } = await supabase
        .from('internal_users')
        .select('username, role')
        .eq('id', userId)
        .maybeSingle();

      if (internalUser) {
        return {
          username: internalUser.username,
          first_name: '',
          last_name: '',
          user_type: 'internal' as const,
          role: internalUser.role
        };
      }

      return {
        username: 'Unknown User',
        first_name: '',
        last_name: '',
        user_type: 'unknown' as const,
        role: 'unknown'
      };
    } catch (error) {
      console.error('Error fetching user details:', error);
      return {
        username: 'Unknown User',
        first_name: '',
        last_name: '',
        user_type: 'unknown' as const,
        role: 'unknown'
      };
    }
  };

  const getUserAvatarUrl = async (userId: string) => {
    try {
      // Avatar URLs are not stored in the current database schema
      // Return null for now - avatars will use initials fallback
      return null;
    } catch (error) {
      console.error('Error fetching avatar URL:', error);
      return null;
    }
  };

  const searchForPlayers = async (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    try {
      setSearching(true);
      const searchResults: TeamMember[] = [];

      // Search Supabase profiles
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, username, first_name, last_name, role')
        .or(`username.ilike.%${query}%,first_name.ilike.%${query}%,last_name.ilike.%${query}%`)
        .limit(10);

      profiles?.forEach(profile => {
        // Don't include players already on the team or already selected
        if (!teamMembers.some(m => m.user_id === profile.id) && !selectedMembers.includes(profile.id)) {
          searchResults.push({
            user_id: profile.id,
            member_type: 'player',
            user_details: {
              username: profile.username,
              first_name: profile.first_name,
              last_name: profile.last_name,
              user_type: 'supabase' as const,
              role: profile.role
            }
          });
        }
      });

      // Search internal users
      const { data: internalUsers } = await supabase
        .from('internal_users')
        .select('id, username, role')
        .ilike('username', `%${query}%`)
        .limit(10);

      internalUsers?.forEach(user => {
        // Don't include players already on the team or already selected
        if (!teamMembers.some(m => m.user_id === user.id) && !selectedMembers.includes(user.id)) {
          searchResults.push({
            user_id: user.id,
            member_type: 'player',
            user_details: {
              username: user.username,
              user_type: 'internal' as const,
              role: user.role
            }
          });
        }
      });

      setSearchResults(searchResults);
    } catch (error) {
      console.error('Error searching for players:', error);
      toast.error('Failed to search for players');
    } finally {
      setSearching(false);
    }
  };

  const handleAddPlayer = (player: TeamMember) => {
    setSelectedMembers(prev => [...prev, player.user_id]);
    setTeamMembers(prev => [...prev, player]);
    setSearchResults(prev => prev.filter(p => p.user_id !== player.user_id));
    setSearchQuery('');
    toast.success(`Added ${getMemberDisplayName(player)} to roster`);
  };

  // Debounced search effect
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.trim()) {
        searchForPlayers(searchQuery);
      } else {
        setSearchResults([]);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, teamMembers, selectedMembers]);

  const handleMemberToggle = async (memberId: string) => {
    if (!canManage) {
      toast.error('You do not have permission to manage this roster');
      return;
    }

    // Check for dual role conflict
    const member = teamMembers.find(m => m.user_id === memberId);
    if (member && userTeam) {
      try {
        const { data: hasConflict } = await supabase
          .rpc('check_dual_role_conflict', {
            p_game_id: gameId,
            p_player_id: memberId,
            p_team_side: userTeam.teamSide,
            p_operation: 'roster'
          });

        if (hasConflict) {
          toast.error(`${getMemberDisplayName(member)} is already a coach for this team and cannot be added as a player. Please choose either coach or player role.`);
          return;
        }
      } catch (error) {
        console.error('Error checking dual role conflict:', error);
      }
    }
    
    setSelectedMembers(prev => 
      prev.includes(memberId) 
        ? prev.filter(id => id !== memberId)
        : [...prev, memberId]
    );
  };

  const handleRemoveMember = async (memberId: string) => {
    if (!canManage) {
      toast.error('You do not have permission to manage this roster');
      return;
    }

    const member = teamMembers.find(m => m.user_id === memberId);
    const memberName = getMemberDisplayName(member || { user_id: memberId, member_type: 'player', user_details: { username: 'Unknown' } } as TeamMember);
    
    if (confirm(`Remove ${memberName} from the roster?`)) {
      try {
        // Remove from game_rosters and game_participants
        await Promise.all([
          supabase
            .from('game_rosters')
            .delete()
            .eq('game_id', gameId)
            .eq('player_id', memberId),
          supabase
            .from('game_participants')
            .delete()
            .eq('game_id', gameId)
            .eq('player_id', memberId)
        ]);

        // Update local state
        setSelectedMembers(prev => prev.filter(id => id !== memberId));
        toast.success(`Removed ${memberName} from roster`);
      } catch (error) {
        console.error('Error removing member:', error);
        toast.error('Failed to remove member from roster');
      }
    }
  };

  const handleSubmitRoster = async () => {
    if (!canManage || !userTeam) {
      toast.error('You do not have permission to manage this roster');
      return;
    }

    try {
      setSubmitting(true);

      // Remove existing roster entries for this team/game
      await Promise.all([
        supabase
          .from('game_rosters')
          .delete()
          .eq('game_id', gameId)
          .eq('team_side', userTeam.teamSide), // Use team_side for venue games
        supabase
          .from('game_participants')
          .delete()
          .eq('game_id', gameId)
          .eq('team_side', userTeam.teamSide) // Use team_side for venue games
      ]);

      // Add selected members to both game roster and participants
      if (selectedMembers.length > 0) {
        // Determine player type for each member
        const rosterEntries = selectedMembers.map(memberId => {
          const member = teamMembers.find(m => m.user_id === memberId);
          const playerType = member?.user_details?.user_type === 'internal' ? 'internal' : 'supabase';
          
          return {
            game_id: gameId,
            team_id: userTeam.teamId,
            player_id: memberId,
            team_side: userTeam.teamSide,
            player_type: playerType,
            added_by: currentUserId,
            is_eligible: true,
            eligibility_status: 'eligible'
          };
        });

        const participantEntries = selectedMembers.map(memberId => {
          const member = teamMembers.find(m => m.user_id === memberId);
          const playerType = member?.user_details?.user_type === 'internal' ? 'internal' : 'supabase';
          
          return {
            game_id: gameId,
            player_id: memberId,
            player_type: playerType,
            team_id: userTeam.teamId,
            team_side: userTeam.teamSide,
            participation_status: 'confirmed',
            eligibility_status: 'eligible',
            requested_by: currentUserId,
            confirmed_by: currentUserId,
            status_updated_at: new Date().toISOString()
          };
        });

        const [rosterResult, participantResult] = await Promise.all([
          supabase.from('game_rosters').insert(rosterEntries),
          supabase.from('game_participants').insert(participantEntries)
        ]);

        if (rosterResult.error) {
          console.error('Roster insert error:', rosterResult.error);
          throw rosterResult.error;
        }

        if (participantResult.error) {
          console.error('Participants insert error:', participantResult.error);
          throw participantResult.error;
        }
      }

      toast.success('Game roster updated successfully');
      // Don't close immediately, allow position management
      // onClose();
    } catch (error) {
      console.error('Error submitting roster:', error);
      toast.error('Failed to update game roster: ' + (error.message || 'Unknown error'));
    } finally {
      setSubmitting(false);
    }
  };

  const getMemberDisplayName = (member: TeamMember) => {
    const details = member.user_details;
    return details?.username || 
           `${details?.first_name || ''} ${details?.last_name || ''}`.trim() ||
           'Unknown Member';
  };

  const getMemberIcon = (type: string) => {
    switch (type) {
      case 'coach':
        return <Shield className="h-4 w-4" />;
      case 'support':
        return <Heart className="h-4 w-4" />;
      default:
        return <Users className="h-4 w-4" />;
    }
  };

  const getMemberTypeColor = (type: string) => {
    switch (type) {
      case 'coach':
        return 'border-blue-500 text-blue-400';
      case 'support':
        return 'border-purple-500 text-purple-400';
      default:
        return 'border-sports-green text-sports-green';
    }
  };

  const players = teamMembers.filter(m => m.member_type === 'player');
  const coaches = teamMembers.filter(m => m.member_type === 'coach');
  const supportStaff = teamMembers.filter(m => m.member_type === 'support');

  // Show position manager if requested
  if (showPositionManager && userTeam && game) {
    return (
      <GamePositionManager
        gameId={gameId}
        gameCode={game.game_code || ''}
        gameTitle={game.game_name || ''}
        teamSide={userTeam.teamSide}
        onBack={() => setShowPositionManager(false)}
      />
    );
  }

  if (!canManage && !loading && userTeam) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-md bg-gray-900 border-gray-700">
          <DialogHeader>
            <DialogTitle className="text-red-400">Access Denied</DialogTitle>
          </DialogHeader>
          <div className="py-6">
            <p className="text-gray-300">
              You do not have permission to manage the roster for this team. 
              Only team owners and coaches can modify rosters.
            </p>
          </div>
          <div className="flex justify-end">
            <Button onClick={onClose} variant="outline" className="border-gray-600 text-gray-300">
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (!userTeam && !loading) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-md bg-gray-900 border-gray-700">
          <DialogHeader>
            <DialogTitle className="text-red-400">No Team Access</DialogTitle>
          </DialogHeader>
          <div className="py-6">
            <p className="text-gray-300">
              You are not a coach or owner of any team in this game.
            </p>
          </div>
          <div className="flex justify-end">
            <Button onClick={onClose} variant="outline" className="border-gray-600 text-gray-300">
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl bg-gray-900 border-gray-700 max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-sports-green">
            Manage Game Roster - {userTeam?.teamName} ({userTeam?.teamSide} team)
          </DialogTitle>
          <p className="text-sm text-gray-400 mt-2">
            Select team members who will participate in this game. This roster determines who is eligible to play and will be validated by venue staff before the game starts.
          </p>
        </DialogHeader>

        <div className="space-y-6">
          {loading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-sports-green"></div>
              <p className="mt-2 text-gray-400">Loading team members...</p>
            </div>
          ) : (
            <>
              {/* Players Section */}
              {players.length > 0 && (
                <Card className="bg-gray-800 border-gray-700">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Users className="h-5 w-5 text-sports-green" />
                      Players ({selectedMembers.filter(id => players.find(p => p.user_id === id)).length}/{players.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {players.map((member) => (
                        <div
                          key={member.user_id}
                          className="flex items-center gap-3 p-3 bg-gray-700 rounded-lg"
                        >
                           <Checkbox
                             id={member.user_id}
                             checked={selectedMembers.includes(member.user_id)}
                             onCheckedChange={() => handleMemberToggle(member.user_id)}
                             className="border-sports-green data-[state=checked]:bg-sports-green"
                           />
                           <div className="flex-1">
                             <div className="font-medium text-white">
                               {getMemberDisplayName(member)}
                             </div>
                             {member.position && (
                               <Badge variant="outline" className={getMemberTypeColor('player')}>
                                 {member.position}
                               </Badge>
                             )}
                           </div>
                           <div className="flex items-center gap-2">
                             {selectedMembers.includes(member.user_id) ? (
                               <>
                                 <UserCheck className="h-5 w-5 text-sports-green" />
                                 <Button
                                   size="sm"
                                   variant="outline"
                                   onClick={() => handleRemoveMember(member.user_id)}
                                   className="border-red-500 text-red-400 hover:bg-red-900 px-2 py-1 text-xs"
                                 >
                                   Remove
                                 </Button>
                               </>
                             ) : (
                               <UserX className="h-5 w-5 text-gray-400" />
                             )}
                           </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Coaches Section */}
              {coaches.length > 0 && (
                <Card className="bg-gray-800 border-gray-700">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Shield className="h-5 w-5 text-blue-400" />
                      Coaches ({selectedMembers.filter(id => coaches.find(c => c.user_id === id)).length}/{coaches.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {coaches.map((member) => (
                        <div
                          key={member.user_id}
                          className="flex items-center gap-3 p-3 bg-gray-700 rounded-lg"
                        >
                           <Checkbox
                             id={member.user_id}
                             checked={selectedMembers.includes(member.user_id)}
                             onCheckedChange={() => handleMemberToggle(member.user_id)}
                             className="border-blue-400 data-[state=checked]:bg-blue-400"
                           />
                           <div className="flex-1">
                             <div className="font-medium text-white">
                               {getMemberDisplayName(member)}
                             </div>
                             {member.role && (
                               <Badge variant="outline" className={getMemberTypeColor('coach')}>
                                 {member.role.replace('_', ' ')}
                               </Badge>
                             )}
                           </div>
                           <div className="flex items-center gap-2">
                             {selectedMembers.includes(member.user_id) ? (
                               <>
                                 <UserCheck className="h-5 w-5 text-blue-400" />
                                 <Button
                                   size="sm"
                                   variant="outline"
                                   onClick={() => handleRemoveMember(member.user_id)}
                                   className="border-red-500 text-red-400 hover:bg-red-900 px-2 py-1 text-xs"
                                 >
                                   Remove
                                 </Button>
                               </>
                             ) : (
                               <UserX className="h-5 w-5 text-gray-400" />
                             )}
                           </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Add Players Section */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-white flex items-center">
                    <Plus className="mr-2 h-5 w-5" />
                    Add Players
                  </h3>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowAddPlayers(!showAddPlayers)}
                  >
                    {showAddPlayers ? 'Hide' : 'Show'} Search
                  </Button>
                </div>

                {showAddPlayers && (
                  <div className="space-y-4">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        placeholder="Search for players by name or username..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10 bg-gray-700 border-gray-600 text-white"
                      />
                    </div>

                    {searching && (
                      <div className="text-center text-gray-400">Searching...</div>
                    )}

                    {searchResults.length > 0 && (
                      <div className="space-y-2">
                        <h4 className="text-sm font-medium text-gray-300">Search Results:</h4>
                        {searchResults.map((player) => (
                          <Card key={player.user_id} className="bg-gray-700 border-gray-600">
                            <CardContent className="p-3">
                               <div className="flex items-center justify-between">
                                 <div className="flex items-center space-x-3">
                                   <Avatar className="h-8 w-8">
                                     <AvatarImage src={player.user_details?.avatar_url} />
                                     <AvatarFallback className="bg-primary/10 text-primary text-sm">
                                       {player.user_details?.first_name?.[0] || player.user_details?.username?.[0] || 'P'}
                                     </AvatarFallback>
                                   </Avatar>
                                    <div>
                                      <p className="text-white font-medium">{getMemberDisplayName(player)}</p>
                                      <div className="flex gap-1">
                                        <Badge variant="outline" className="text-xs border-blue-500 text-blue-400">
                                          {player.user_details?.role || 'Player'}
                                        </Badge>
                                        <Badge variant="outline" className="text-xs border-gray-500 text-gray-400">
                                          {getUserTypeDisplayName(player.user_details?.user_type)}
                                        </Badge>
                                      </div>
                                    </div>
                                 </div>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleAddPlayer(player)}
                                  className="bg-green-600 hover:bg-green-700 text-white border-green-600"
                                >
                                  <Plus className="h-4 w-4 mr-1" />
                                  Add
                                </Button>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    )}

                    {searchQuery && !searching && searchResults.length === 0 && (
                      <div className="text-center text-gray-400">No players found</div>
                    )}
                  </div>
                )}
              </div>

              {teamMembers.length === 0 && (
                <Card className="bg-gray-800 border-gray-700">
                  <CardContent className="text-center py-8">
                    <Users className="h-12 w-12 text-gray-500 mx-auto mb-4" />
                    <p className="text-gray-400">No team members found</p>
                  </CardContent>
                </Card>
              )}

              <div className="flex items-center justify-between bg-gray-800 p-4 rounded-lg">
                <div className="text-sm text-gray-400">
                  <div className="font-semibold text-white mb-1">Selection Summary:</div>
                  <div>Players: {selectedMembers.filter(id => teamMembers.find(m => m.user_id === id && m.member_type === 'player')).length}</div>
                  <div>Coaches: {selectedMembers.filter(id => teamMembers.find(m => m.user_id === id && m.member_type === 'coach')).length}</div>
                  <div>Total Selected: {selectedMembers.length} of {teamMembers.length}</div>
                </div>
                <div className="flex gap-3">
                  <Button
                    onClick={onClose}
                    variant="outline"
                    className="border-gray-600 text-gray-300 hover:bg-gray-800"
                  >
                    Close
                  </Button>
                  {selectedMembers.length > 0 && !submitting && (
                    <Button
                      onClick={() => {
                        console.log('🎯 Manage Positions clicked with:', { gameId, userTeam, game });
                        setShowPositionManager(true);
                      }}
                      variant="outline"
                      className="border-blue-500 text-blue-400 hover:bg-blue-900"
                    >
                      <MapPin className="h-4 w-4 mr-2" />
                      Manage Positions
                    </Button>
                  )}
                  <Button
                    onClick={handleSubmitRoster}
                    disabled={submitting || !canManage}
                    className="bg-sports-green text-black hover:bg-green-600"
                  >
                    {submitting ? 'Updating...' : 'Update Roster'}
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};